import i18next from 'i18next';

import { GraphColorsInOrder, graphSettings } from '@/config/settings';

export const MIN_RELATIVE_COUNT = 0.0001;

const BIOSCOPE_STATUS_NAMESPACE = 'bioscopeStatus';
const BIOSCOPE_IMAGE_CLASSES_KEY_PREFIX = 'bioscopeImageClasses';
const SCENE_LABEL_TAG_KEY_PREFIX = 'sceneLabelTag';

/**
 * Maps raw bioscope scene-label tags to grouped high-level categories.
 * Keep in sync with backend groupedLabelMap in cage.controller.ts (WB-1311).
 */
export const SCENE_LABEL_GROUP_MAP: Record<string, string> = {
    school: 'optimal',
    singlefish: 'singleFish',
    empty: 'farAway',
    faraway: 'farAway',
    tooclose: 'tooClose',
    chaos: 'tooClose',
    net: 'blocked',
    rope: 'blocked',
    shadow: 'blocked',
    ufo: 'blocked',
    badangle: 'badAngle',
    backlight: 'badAngle',
    blurry: 'blurry',
    dew: 'blurry',
};

export function getCanonicalSceneLabelTag(tag: string): string {
    return SCENE_LABEL_GROUP_MAP[tag.toLowerCase()] ?? tag;
}

export function isAliasedSceneLabelTag(tag: string): boolean {
    const mappedTag = SCENE_LABEL_GROUP_MAP[tag.toLowerCase()];
    return mappedTag != null && mappedTag !== tag;
}

export function findSceneLabelTagIndex(
    sceneLabelTags: string[] | undefined,
    tagKey: string
): number {
    const normalizedTagKey = tagKey.toLowerCase();
    return sceneLabelTags?.findIndex((tag) => tag.toLowerCase() === normalizedTagKey) ?? -1;
}

export function getConfiguredSceneLabelColor(tag: string): string | undefined {
    const canonicalTag = getCanonicalSceneLabelTag(tag);

    return (
        (graphSettings.SceneLabelTagsColors as Record<string, string | undefined>)[canonicalTag] ??
        (graphSettings.SceneLabelTagsColors as Record<string, string | undefined>)[tag]
    );
}

export function resolveSceneLabelColor(tag: string, fallbackIndex: number): string {
    const configuredColor = getConfiguredSceneLabelColor(tag);
    if (configuredColor) {
        return configuredColor;
    }

    return GraphColorsInOrder[fallbackIndex % GraphColorsInOrder.length];
}

function resolveRootSceneLabelTagName(tag: string): string | undefined {
    const translationKey = `${SCENE_LABEL_TAG_KEY_PREFIX}.${tag}`;

    if (i18next.exists(translationKey)) {
        return i18next.t(translationKey);
    }

    return undefined;
}

function resolveBioscopeImageClassName(tag: string): string | undefined {
    const translationKey = `${BIOSCOPE_IMAGE_CLASSES_KEY_PREFIX}.${tag}`;
    const namespacedKey = `${BIOSCOPE_STATUS_NAMESPACE}:${translationKey}`;

    if (i18next.exists(namespacedKey)) {
        return i18next.t(namespacedKey);
    }

    return undefined;
}

/** Display name for Bioscope Status pie chart (grouped labels from overview.labels). */
export function resolveSceneLabelName(tag: string): string {
    return resolveBioscopeImageClassName(tag) ?? tag;
}

/** Display name for admin Scene labels graph (raw per-day tags from overview.days). */
export function resolveSceneLabelGraphName(tag: string): string {
    const canonicalTag = getCanonicalSceneLabelTag(tag);
    const graphName = resolveRootSceneLabelTagName(canonicalTag);
    const bioscopeName = resolveBioscopeImageClassName(canonicalTag);

    if (isAliasedSceneLabelTag(tag)) {
        const displayNames = [graphName, bioscopeName].filter(
            (name, index, names): name is string => name != null && names.indexOf(name) === index
        );
        const primaryName = displayNames.length > 0 ? displayNames.join(' / ') : canonicalTag;

        return `${primaryName} (${tag})`;
    }

    return graphName ?? bioscopeName ?? tag;
}
